import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import VendorCard from '../components/VendorCard';
import { toast } from 'sonner';
import { 
  Search, 
  MapPin, 
  Building2, 
  Camera, 
  Sparkles, 
  Flower2, 
  Music, 
  UtensilsCrossed,
  ArrowRight,
  Star,
  Heart,
  Crown,
  Phone,
  Calendar,
  Send,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  venues: Building2,
  photographers: Camera,
  makeup: Sparkles,
  mehndi: Sparkles,
  decorators: Flower2,
  dj: Music,
  catering: UtensilsCrossed,
  'bridal-wear': Crown,
  invitations: Heart,
  'photo-booth': Camera,
  entertainment: Music,
};

const rugPatterns = {
  rug1: 'https://images.unsplash.com/photo-1652634213812-f0deeb1de78e?w=1920&q=80',
  rug2: 'https://images.unsplash.com/photo-1671063170050-c898f39a5a80?w=1920&q=80',
  rug3: 'https://images.unsplash.com/photo-1710924447533-7177c52b3da3?w=1920&q=80',
  rug4: 'https://images.unsplash.com/photo-1594847915592-2a7ef568e2b6?w=1920&q=80',
};

const HomePage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [featuredVendors, setFeaturedVendors] = useState([]);
  const [searchCategory, setSearchCategory] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Shaadi Genie form state
  const [shaadiGenieForm, setShaadiGenieForm] = useState({
    name: '',
    phone: '',
    email: '',
    vendor_types: [],
    event_date: '',
    location: '',
    city: '',
    message: ''
  });
  const [submittingLead, setSubmittingLead] = useState(false);
  const [showShaadiGenieForm, setShowShaadiGenieForm] = useState(false);

  useEffect(() => {
    fetchData();
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);

  const fetchData = async () => {
    try {
      await axios.post(`${API}/seed`);
      const [categoriesRes, vendorsRes] = await Promise.all([
        axios.get(`${API}/vendors/categories`),
        axios.get(`${API}/vendors/featured?limit=6`),
      ]);
      setCategories(categoriesRes.data.categories || []);
      setCities(categoriesRes.data.cities || []);
      setFeaturedVendors(vendorsRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchCategory && searchCategory !== 'all') params.set('category', searchCategory);
    if (searchCity && searchCity !== 'all') params.set('city', searchCity);
    navigate(`/vendors?${params.toString()}`);
  };

  const handleShaadiGenieSubmit = async (e) => {
    e.preventDefault();
    if (!shaadiGenieForm.name || !shaadiGenieForm.phone || !shaadiGenieForm.email) {
      toast.error('Please fill in your name, phone number and email');
      return;
    }
    setSubmittingLead(true);
    try {
      await axios.post(`${API}/shaadi-genie`, shaadiGenieForm);
      toast.success('Thank you! We will contact you soon.');
      setShaadiGenieForm({ name: '', phone: '', email: '', vendor_types: [], event_date: '', location: '', city: '', message: '' });
      setShowShaadiGenieForm(false);
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmittingLead(false);
    }
  };

  const toggleVendorType = (type) => {
    setShaadiGenieForm(prev => ({
      ...prev,
      vendor_types: prev.vendor_types.includes(type) 
        ? prev.vendor_types.filter(t => t !== type)
        : [...prev.vendor_types, type]
    }));
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      <style>{`
        .hero-video {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .rug-section {
          position: relative;
        }
        .rug-bg {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
        }
        .rug-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.45);
        }
        .rug-fade-top {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 100px;
          background: linear-gradient(to bottom, rgba(0,0,0,0.7), transparent);
          z-index: 5;
        }
        .rug-fade-bottom {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 100px;
          background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
          z-index: 5;
        }
        .section-content {
          position: relative;
          z-index: 10;
        }
        .cursive-text {
          font-family: 'Dancing Script', cursive;
        }
      `}</style>

      {/* HERO - Video Background - NO BORDER */}
      <section className="relative min-h-[90vh] md:min-h-screen flex items-center justify-center overflow-hidden">
        <video className="hero-video" autoPlay muted loop playsInline preload="metadata" poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'%3E%3Crect fill='%238b1a3b' width='1920' height='1080'/%3E%3C/svg%3E">
          <source src="https://customer-assets.emergentagent.com/job_wedding-hub-29/artifacts/3f5qvm08_AZhiOF0r1U0-yiRsO41ZVQ-AZhiOF0ruq0x6w_sRI3pvg.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-maroon-900/40 via-black/40 to-black/70 z-[1]" />
        
        <div className="relative z-10 text-center px-4 max-w-5xl">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-bold text-white leading-none mb-4 md:mb-6"
            style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '3px', textShadow: '3px 3px 30px rgba(0,0,0,0.8)' }}
          >
            YOUR DREAM<br />
            <span className="cursive-text text-gold-400 text-5xl sm:text-6xl md:text-7xl lg:text-9xl">South Asian</span><br />
            WEDDING AWAITS
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-base md:text-xl text-white/90 mb-6 md:mb-10 max-w-2xl mx-auto px-4"
          >
            Discover extraordinary vendors, timeless traditions, and create memories that last forever.
          </motion.p>

          <motion.form 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            onSubmit={handleSearch} 
            className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto px-4"
            data-testid="hero-search-form"
          >
            <div className="flex-1">
              <Select value={searchCategory || "all"} onValueChange={setSearchCategory}>
                <SelectTrigger className="w-full h-12 md:h-14 rounded-xl bg-white/95 border-0 text-maroon-900 font-medium" data-testid="search-category">
                  <div className="flex items-center gap-2">
                    <Search className="w-4 h-4 md:w-5 md:h-5 text-maroon-600" />
                    <SelectValue placeholder="What are you looking for?" />
                  </div>
                </SelectTrigger>
                <SelectContent className="rounded-xl z-50">
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1">
              <Select value={searchCity || "all"} onValueChange={setSearchCity}>
                <SelectTrigger className="w-full h-12 md:h-14 rounded-xl bg-white/95 border-0 text-maroon-900 font-medium" data-testid="search-city">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 md:w-5 md:h-5 text-maroon-600" />
                    <SelectValue placeholder="Select City" />
                  </div>
                </SelectTrigger>
                <SelectContent className="rounded-xl z-50 max-h-60">
                  <SelectItem value="all">All Cities</SelectItem>
                  {cities.map((city) => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              type="submit" 
              className="h-12 md:h-14 px-6 md:px-10 text-base md:text-lg font-bold rounded-xl"
              style={{ fontFamily: "'Bebas Neue', sans-serif", background: 'linear-gradient(135deg, #d4af37, #c5a028)', color: '#3d0a18', letterSpacing: '2px' }}
              data-testid="search-btn"
            >
              <Search className="w-4 h-4 md:w-5 md:h-5 mr-2" /> EXPLORE
            </Button>
          </motion.form>
        </div>
        
        {/* Gradient to blend into next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent z-[2]" />
      </section>

      {/* CATEGORIES - Rug 1 - Pulled up with negative margin */}
      <section className="rug-section py-16 md:py-24 -mt-16">
        <div className="rug-bg" style={{ backgroundImage: `url(${rugPatterns.rug1})` }} />
        <div className="rug-overlay" />
        <div className="rug-fade-bottom" />
        
        <div className="section-content max-w-7xl mx-auto px-4">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-8 md:mb-14 text-center"
            style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '3px', textShadow: '2px 2px 20px rgba(0,0,0,0.5)' }}
          >
            FIND YOUR <span className="text-gold-400">PERFECT</span> VENDORS
          </motion.h2>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {categories.slice(0, 5).map((category, idx) => {
              const IconComponent = categoryIcons[category.id] || Building2;
              return (
                <motion.div key={category.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}>
                  <Link to={`/vendors?category=${category.id}&city=Montreal`} className="block p-4 md:p-8 rounded-xl md:rounded-2xl bg-white/95 border-2 border-transparent hover:border-gold-400 hover:shadow-xl transition-all text-center group" data-testid={`category-${category.id}`}>
                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-gradient-to-br from-maroon-100 to-cream-100 flex items-center justify-center mx-auto mb-2 md:mb-4 group-hover:from-gold-100 group-hover:to-gold-50 transition-colors">
                      <IconComponent className="w-6 h-6 md:w-8 md:h-8 text-maroon-700 group-hover:text-gold-600 transition-colors" />
                    </div>
                    <h3 className="text-sm md:text-xl text-maroon-900 group-hover:text-gold-700" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '1px' }}>{category.name.toUpperCase()}</h3>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SHAADI GENIE Section - Moved up before Featured Vendors */}
      <section className="rug-section py-16 md:py-24" id="shaadi-genie">
        <div className="rug-bg" style={{ backgroundImage: `url(${rugPatterns.rug3})` }} />
        <div className="rug-overlay" />
        <div className="rug-fade-top" />
        <div className="rug-fade-bottom" />
        
        <div className="section-content max-w-4xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '3px', textShadow: '2px 2px 20px rgba(0,0,0,0.5)' }} data-testid="shaadi-genie-title">
              SHAADI <span className="text-gold-400">GENIE</span>
            </h2>
            <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto mb-6" style={{ fontFamily: "'Dancing Script', cursive" }}>
              Let us help you find the perfect vendors for your dream wedding
            </p>
            
            {!showShaadiGenieForm && (
              <Button 
                onClick={() => setShowShaadiGenieForm(true)}
                className="h-14 px-10 text-lg rounded-xl"
                style={{ fontFamily: "'Bebas Neue', sans-serif", background: 'linear-gradient(135deg, #d4af37, #c5a028)', color: '#3d0a18', letterSpacing: '2px' }}
                data-testid="shaadi-genie-cta-btn"
              >
                <Phone className="w-5 h-5 mr-2" /> GET FREE CONSULTATION
              </Button>
            )}
          </motion.div>

          {showShaadiGenieForm && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} 
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white/95 backdrop-blur-sm rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-2xl border-2 border-gold-300"
              data-testid="shaadi-genie-form"
            >
              <form onSubmit={handleShaadiGenieSubmit} className="space-y-5">
                {/* Contact Info */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-maroon-800 mb-1 block">Your Name *</label>
                    <Input 
                      value={shaadiGenieForm.name} 
                      onChange={(e) => setShaadiGenieForm({...shaadiGenieForm, name: e.target.value})}
                      placeholder="Enter your name"
                      className="h-11 rounded-xl border-gold-200 focus:border-gold-400"
                      required
                      data-testid="shaadi-genie-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-maroon-800 mb-1 block">Phone Number *</label>
                    <Input 
                      value={shaadiGenieForm.phone} 
                      onChange={(e) => setShaadiGenieForm({...shaadiGenieForm, phone: e.target.value})}
                      placeholder="(416) 555-0123"
                      className="h-11 rounded-xl border-gold-200 focus:border-gold-400"
                      required
                      data-testid="shaadi-genie-phone"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-maroon-800 mb-1 block">Email *</label>
                    <Input 
                      type="email"
                      value={shaadiGenieForm.email} 
                      onChange={(e) => setShaadiGenieForm({...shaadiGenieForm, email: e.target.value})}
                      placeholder="your@email.com"
                      className="h-11 rounded-xl border-gold-200 focus:border-gold-400"
                      required
                      data-testid="shaadi-genie-email"
                    />
                  </div>
                </div>

                {/* Event Details */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-maroon-800 mb-1 block flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Event Date
                    </label>
                    <Input 
                      type="date"
                      value={shaadiGenieForm.event_date} 
                      onChange={(e) => setShaadiGenieForm({...shaadiGenieForm, event_date: e.target.value})}
                      className="h-11 rounded-xl border-gold-200 focus:border-gold-400"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-maroon-800 mb-1 block flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> City
                    </label>
                    <Select value={shaadiGenieForm.city} onValueChange={(v) => setShaadiGenieForm({...shaadiGenieForm, city: v})}>
                      <SelectTrigger className="h-11 rounded-xl border-gold-200">
                        <SelectValue placeholder="Select City" />
                      </SelectTrigger>
                      <SelectContent>
                        {cities.map((city) => (
                          <SelectItem key={city} value={city}>{city}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Vendor Types */}
                <div>
                  <label className="text-sm font-medium text-maroon-800 mb-2 block">What vendors are you looking for?</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                    {categories.map((cat) => (
                      <label 
                        key={cat.id}
                        className={`flex items-center gap-2 p-2.5 rounded-lg border-2 cursor-pointer transition-all text-sm ${
                          shaadiGenieForm.vendor_types.includes(cat.id) 
                            ? 'border-gold-500 bg-gold-50' 
                            : 'border-gray-200 hover:border-gold-300'
                        }`}
                      >
                        <Checkbox 
                          checked={shaadiGenieForm.vendor_types.includes(cat.id)}
                          onCheckedChange={() => toggleVendorType(cat.id)}
                          className="border-gold-400 data-[state=checked]:bg-gold-500 h-4 w-4"
                        />
                        <span className="text-xs text-maroon-800">{cat.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Submit */}
                <div className="flex items-center gap-3 pt-2">
                  <Button 
                    type="submit" 
                    disabled={submittingLead}
                    className="h-12 px-8 rounded-xl flex-1"
                    style={{ fontFamily: "'Bebas Neue', sans-serif", background: 'linear-gradient(135deg, #8B1538, #6d102c)', color: 'white', letterSpacing: '2px' }}
                    data-testid="shaadi-genie-submit"
                  >
                    {submittingLead ? 'Submitting...' : <><Send className="w-4 h-4 mr-2" /> SUBMIT</>}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setShowShaadiGenieForm(false)}
                    className="h-12 px-6 rounded-xl"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </motion.div>
          )}
        </div>
      </section>

      {/* FEATURED VENDORS - Rug 2 */}
      <section className="rug-section py-16 md:py-24">
        <div className="rug-bg" style={{ backgroundImage: `url(${rugPatterns.rug2})` }} />
        <div className="rug-overlay" />
        <div className="rug-fade-top" />
        <div className="rug-fade-bottom" />
        
        <div className="section-content max-w-7xl mx-auto px-4">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-8 md:mb-14 text-center" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '3px', textShadow: '2px 2px 20px rgba(0,0,0,0.5)' }}>
            FEATURED <span className="text-gold-400">VENDORS</span>
          </motion.h2>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
              {[...Array(3)].map((_, i) => (<div key={`skel-${i}`} className="bg-white/20 rounded-2xl aspect-[4/3] animate-pulse" />))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
              {featuredVendors.slice(0, 6).map((vendor, idx) => (
                <motion.div key={vendor.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}>
                  <VendorCard vendor={vendor} />
                </motion.div>
              ))}
            </div>
          )}

          <div className="mt-8 md:mt-12 text-center">
            <Link to="/vendors?city=Montreal">
              <Button className="h-12 md:h-14 px-8 md:px-10 text-base md:text-lg rounded-xl" style={{ fontFamily: "'Bebas Neue', sans-serif", background: 'linear-gradient(135deg, #d4af37, #c5a028)', color: '#3d0a18', letterSpacing: '2px' }}>
                VIEW ALL VENDORS <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* WHY US - Rug 3 */}
      <section className="rug-section py-16 md:py-24">
        <div className="rug-bg" style={{ backgroundImage: `url(${rugPatterns.rug3})` }} />
        <div className="rug-overlay" />
        <div className="rug-fade-top" />
        <div className="rug-fade-bottom" />
        
        <div className="section-content max-w-7xl mx-auto px-4">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-8 md:mb-14 text-center" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '3px', textShadow: '2px 2px 20px rgba(0,0,0,0.5)' }}>
            WHY COUPLES <span className="text-gold-400">LOVE</span> US
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-4 md:gap-8">
            {[
              { icon: Crown, title: 'CURATED EXCELLENCE', desc: 'Every vendor is hand-picked and verified to meet our high standards' },
              { icon: Heart, title: 'CULTURAL EXPERTISE', desc: 'Specialists in South Asian traditions and multi-day celebrations' },
              { icon: Star, title: 'TRUSTED REVIEWS', desc: 'Real reviews from real couples to help you decide with confidence' },
            ].map((item, idx) => (
              <motion.div key={`feature-${item.title}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.15 }} className="text-center p-6 md:p-10 rounded-2xl bg-white/10 backdrop-blur-sm border-2 border-gold-500/30 hover:border-gold-500/60 transition-colors">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center mx-auto mb-4 md:mb-6">
                  <item.icon className="w-7 h-7 md:w-8 md:h-8 text-maroon-900" />
                </div>
                <h3 className="text-xl md:text-2xl text-gold-400 mb-2 md:mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '2px' }}>{item.title}</h3>
                <p className="text-white/80 text-sm md:text-base">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVING ALL CANADA - Rug 4 */}
      <section className="rug-section py-16 md:py-24">
        <div className="rug-bg" style={{ backgroundImage: `url(${rugPatterns.rug4})` }} />
        <div className="rug-overlay" />
        <div className="rug-fade-top" />
        <div className="rug-fade-bottom" />
        
        <div className="section-content max-w-7xl mx-auto px-4">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-8 md:mb-12 text-center" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '3px', textShadow: '2px 2px 20px rgba(0,0,0,0.5)' }}>
            FIND VENDORS IN <span className="text-gold-400">YOUR CITY</span>
          </motion.h2>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
            {cities.slice(0, 10).map((city, idx) => (
              <motion.div key={city} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: idx * 0.05 }} className="p-4 md:p-6 rounded-xl md:rounded-2xl bg-white/90 border-2 border-gold-400/50 text-center hover:border-gold-400 hover:bg-white transition-all cursor-pointer" onClick={() => navigate(`/vendors?city=${encodeURIComponent(city)}`)}>
                <MapPin className="w-6 h-6 md:w-8 md:h-8 text-gold-500 mx-auto mb-2 md:mb-3" />
                <h3 className="text-xs md:text-sm text-maroon-800 font-medium">{city}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Rug 1 */}
      <section className="rug-section py-16 md:py-24">
        <div className="rug-bg" style={{ backgroundImage: `url(${rugPatterns.rug1})` }} />
        <div className="rug-overlay" />
        <div className="rug-fade-top" />
        
        <div className="section-content max-w-5xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="bg-maroon-900/90 border-4 border-gold-500 rounded-2xl md:rounded-3xl p-8 md:p-12 lg:p-16 text-center">
            <Crown className="w-12 h-12 md:w-16 md:h-16 text-gold-400 mx-auto mb-4 md:mb-6" />
            <h2 className="text-2xl md:text-4xl lg:text-5xl text-white mb-3 md:mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '2px' }}>ARE YOU A WEDDING VENDOR?</h2>
            <p className="text-white/85 text-sm md:text-lg mb-6 md:mb-8 max-w-2xl mx-auto">Join Montreal's most exclusive wedding marketplace and connect with couples ready to celebrate.</p>
            <Link to="/become-vendor">
              <Button className="h-12 md:h-14 px-8 md:px-12 text-base md:text-xl rounded-xl" style={{ fontFamily: "'Bebas Neue', sans-serif", background: 'linear-gradient(135deg, #d4af37, #c5a028)', color: '#3d0a18', letterSpacing: '2px' }}>
                <Crown className="w-4 h-4 md:w-5 md:h-5 mr-2" /> JOIN OUR MARKETPLACE
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
